import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@material-ui/core';
import { Info as InfoIcon } from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineHtml from '../../../common/inline/InlineHtml';
import InlineFormik from '../../../common/inline/InlineFormik';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import InlineTextField from '../../../common/inline/InlineTextField';
import { useLinkTransform } from '../../../common/HeaderBackButton';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useUpdateProductValidation } from '../Product.hooks';
import {
  ProductValidation,
  BaseProductValidationCardProps
} from '../Product.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';

const ValidationInfoCard: React.FC<BaseProductValidationCardProps> = (
  props: BaseProductValidationCardProps
) => {
  const {
    title = 'Product Validation Info',
    productId,
    productValidation,
    productValidationVersion,
    hidden = false,
    enableTitleRedirect = false,
    onChange,
    onUpdate,
    onError
  } = props;
  const initalData = productValidationVersion || productValidation;
  const initialValues: Partial<ProductValidation> = {
    title: initalData.title || '',
    description: initalData.description || ''
  };
  const getPath = useLinkTransform(
    `/portal/products/${productId}/validations/${productValidation.id}`,
    [{ query: 'from' }]
  );
  const {
    updatedProductValidation,
    isUpdatingProductValidation: isUpdating,
    updateProductValidationError: updateError,
    updateProductValidation
  } = useUpdateProductValidation(productId, productValidation.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(productValidation.task.id || 0);
  const error: FetchError | undefined = updateError || updateTaskError;
  const { isFieldDisabled } = useTaskEntityFields(productValidation.task);

  useEffect(() => {
    if (updatedProductValidation) {
      onChange?.(updatedProductValidation);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProductValidation]);

  useEffect(() => {
    if (updatedTask) {
      productValidation.task = Object.assign(
        productValidation.task || {},
        updatedTask
      );
      onChange?.(productValidation);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTask]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <Card style={hidden ? { display: 'none' } : undefined}>
      <CardTitle>
        <InfoIcon />
        {!enableTitleRedirect ? (
          <Link className='link' to={getPath()}>
            {title}
          </Link>
        ) : (
          title
        )}
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          error={updateError}
          isSubmitting={isUpdating}
          initialValues={initialValues}
          onSubmit={(values: Partial<ProductValidation>) => {
            updateProductValidation(values);
            onChange?.(values);
          }}
        >
          <InlineForm>
            <InlineTextField
              disabled
              name='productId'
              label='Product ID'
              value={`PRODUCT-${productId}`}
            />
            <InlineTextField
              name='title'
              label='Title'
              placeholder='Enter title for Product Validation'
              disabled={
                Boolean(productValidationVersion) ||
                isUpdating ||
                isFieldDisabled('title')
              }
            />
            <InlineHtml
              name='description'
              label='Description'
              helperText='Enter description for Product Validation'
              disabled={
                Boolean(productValidationVersion) ||
                isUpdating ||
                isFieldDisabled('description')
              }
              attachments={productValidation.task.attachments || []}
              onChangeAttachments={(attachments: InlineFileValue[]) => {
                updateTask({ attachments });
                if (productValidation.task) {
                  productValidation.task.attachments = attachments;
                }
                onChange?.(productValidation);
              }}
            />
          </InlineForm>
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default ValidationInfoCard;
